/**
 * Which theme is showing (dark mode, KAN-304).
 *
 * The rule, highest first (public/theme-init.js carries the same rule before first paint):
 *   1. customer pages (/estimate, /unsubscribe) are always Light;
 *   2. a dev-only `?theme=dark|light` override (kept per tab in sessionStorage) — gated on the debug-build
 *      check (`IS_DEBUG_BUILD`: localhost + the dev site, OFF on production builds) BEFORE the URL or
 *      storage is read, so neither a typed param nor a stale value revives it in prod;
 *   3. the device copy of the signed-in user's preference (`cvx:theme-pref`: 'system' | 'light' | 'dark');
 *   4. 'system' = follow the phone/computer (`prefers-color-scheme`).
 * The copy survives sign-out (founder, 2026-10-30): the sign-in screen, splash and app stay in the last person's look, so
 * signing in never switches theme. Only its owner id is cleared at sign-out. A device nobody signed in on follows the
 * device. Never decide this from the hostname.
 * The live state (preference changes, OS changes, forced-light pages) lives in themeController.js.
 */

import { IS_DEBUG_BUILD } from '../../utils/env/debugBuild';

export const THEME_OVERRIDE_PARAM = 'theme';
export const THEME_OVERRIDE_STORAGE_KEY = 'cvx:theme';
/** localStorage key: the signed-in user's saved preference, copied to the device so the first paint is right. */
export const THEME_PREF_STORAGE_KEY = 'cvx:theme-pref';
/** localStorage key: the user id that owns the device copy (a different user never inherits it). */
export const THEME_PREF_USER_STORAGE_KEY = 'cvx:theme-pref-user';
export const THEME_PREFS = ['system', 'light', 'dark'];
/** Attribute that remembers a theme-color meta's Light content (index.html's value) before it is rewritten. */
export const LIGHT_CONTENT_ATTR = 'data-cvx-light-content';

const valid = (v) => (v === 'dark' || v === 'light' ? v : null);
export const validPref = (v) => (THEME_PREFS.includes(v) ? v : null);
const FORCED_LIGHT_PATH = /^\/(estimate|unsubscribe)(\/|$)/;

function getLocalStorage() {
  try { return window.localStorage; } catch { return null; }
}

/** The device copy of the preference, or null when absent/invalid/unreadable. */
export function readDevicePref(storage) {
  try { return validPref((storage === undefined ? getLocalStorage() : storage)?.getItem(THEME_PREF_STORAGE_KEY)); } catch { return null; }
}
export function writeDevicePref(pref, storage, ownerId) {
  try {
    const st = storage === undefined ? getLocalStorage() : storage;
    st?.setItem(THEME_PREF_STORAGE_KEY, pref);
    if (ownerId) st?.setItem(THEME_PREF_USER_STORAGE_KEY, ownerId);
  } catch { /* blocked: this tab still follows the in-memory value */ }
}
export function readDevicePrefOwner(storage) {
  try { return (storage === undefined ? getLocalStorage() : storage)?.getItem(THEME_PREF_USER_STORAGE_KEY) || null; } catch { return null; }
}
/** Sign-out: drop only WHO owns the copy (a user id); the theme word itself stays so the sign-in screen keeps the look. */
export function clearDevicePrefOwner(storage) {
  try { (storage === undefined ? getLocalStorage() : storage)?.removeItem(THEME_PREF_USER_STORAGE_KEY); } catch { /* ignore */ }
}

/** True when the device asks for a dark appearance. Any doubt (no matchMedia, it throws) is Light. */
export function systemPrefersDark() {
  try { return !!window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
}

/**
 * @param {{ debug?: boolean, search?: string, storage?: Storage|null, pathname?: string,
 *           devicePref?: 'system'|'light'|'dark'|null, systemDark?: boolean }} [env] injectable for tests
 * @returns {'light'|'dark'}
 */
export function resolveTheme(env = {}) {
  const pathname = env.pathname === undefined ? (typeof window !== 'undefined' ? window.location.pathname : '') : env.pathname;
  if (FORCED_LIGHT_PATH.test(pathname || '')) return 'light';
  const debug = env.debug === undefined ? IS_DEBUG_BUILD : env.debug;
  if (debug) {
    let storage = env.storage;
    if (storage === undefined) {
      try { storage = window.sessionStorage; } catch { storage = null; }
    }
    const search = env.search === undefined ? (typeof window !== 'undefined' ? window.location.search : '') : env.search;
    let fromUrl = null;
    try { fromUrl = valid(new URLSearchParams(search).get(THEME_OVERRIDE_PARAM)); } catch { fromUrl = null; }
    if (fromUrl) {
      try { storage?.setItem(THEME_OVERRIDE_STORAGE_KEY, fromUrl); } catch { /* storage blocked: the URL still wins this load */ }
      return fromUrl;
    }
    let stored = null;
    try { stored = valid(storage?.getItem(THEME_OVERRIDE_STORAGE_KEY)); } catch { stored = null; }
    if (stored) return stored;
  }
  const pref = env.devicePref === undefined ? readDevicePref() : validPref(env.devicePref);
  if (pref === 'light' || pref === 'dark') return pref;
  const systemDark = env.systemDark === undefined ? systemPrefersDark() : !!env.systemDark;
  return systemDark ? 'dark' : 'light';
}

/** Graphite shell header colour (= GRAPHITE bg.surface); public/theme-init.js carries the same value. */
export const DARK_THEME_COLOR = '#1C1C1E';

/** Dark tints every theme-color meta; Light puts each one back to its own original (captured once, before the first rewrite). */
function applyThemeColorMeta(doc, theme) {
  try {
    doc.querySelectorAll('meta[name="theme-color"]').forEach((m) => {
      if (!m.hasAttribute(LIGHT_CONTENT_ATTR)) {
        if (theme !== 'dark') return; // untouched since index.html: already the Light value
        m.setAttribute(LIGHT_CONTENT_ATTR, m.getAttribute('content') || '');
      }
      m.setAttribute('content', theme === 'dark' ? DARK_THEME_COLOR : m.getAttribute(LIGHT_CONTENT_ATTR));
    });
  } catch { /* the pre-paint script already set it */ }
}

/** Stamps `data-theme` on <html> and the browser-chrome metas. */
export function applyTheme(theme, doc = typeof document !== 'undefined' ? document : null) {
  if (!doc?.documentElement) return;
  doc.documentElement.dataset.theme = theme;
  applyThemeColorMeta(doc, theme);
}

/** Resolves the theme and applies it. Returns the theme. */
export function applyThemeMode(doc = typeof document !== 'undefined' ? document : null, env) {
  const theme = resolveTheme(env);
  applyTheme(theme, doc);
  return theme;
}
